import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme } from '../contexts/ThemeContext'
import 'primeicons/primeicons.css'

interface FAQItem {
  question: string
  answer: string
}

const faqData: FAQItem[] = [
  {
    question: 'Vì sao tôi không bấm điểm danh được?',
    answer: 'Hệ thống chỉ cho phép điểm danh 1 lần sau mỗi 24 giờ. Nếu chưa đủ 24 giờ kể từ lần điểm danh gần nhất, bạn sẽ không thể điểm danh.'
  },
  {
    question: 'Tôi đã upload ảnh nhưng chưa thấy hiện điểm?',
    answer: 'Ảnh và điểm danh sẽ được hệ thống xử lý tự động, có thể mất vài phút đến vài giờ tùy vào tốc độ mạng. Vui lòng không upload lại nhiều lần.'
  },
  {
    question: 'Reset sang tháng mới thì dữ liệu cũ có mất không?',
    answer: 'Ảnh điểm danh của tháng hiện tại sẽ được reset. Bảng xếp hạng tháng cũ vẫn được lưu lại để làm mốc tham khảo và tạo động lực cho người mới.'
  },
  {
    question: 'Nếu nhiều người bằng điểm thì xếp hạng như thế nào?',
    answer: 'Thứ hạng sẽ được ưu tiên theo thứ tự: Tổng điểm → Số buổi tập → Chuỗi max → Thời điểm điểm danh gần nhất.'
  },
  {
    question: 'Chuỗi thưởng (3 – 7 – 14 – 21 – 30 buổi) có cần liên tục không?',
    answer: 'Không cần liên tục. Chỉ cần đủ số buổi tập hợp lệ, bạn sẽ nhận thưởng tương ứng.'
  },
  {
    question: 'Có thể gian lận điểm danh không?',
    answer: 'Hệ thống có các cơ chế kiểm soát tự động. Những hành vi gian lận rõ ràng có thể bị trừ điểm hoặc loại khỏi BXH mà không cần báo trước.'
  },
  {
    question: 'Admin có tham gia bảng xếp hạng không?',
    answer: 'Không. Admin chỉ có quyền quản lý và theo dõi hệ thống, không tham gia cuộc đua điểm số.'
  }
]

export default function FAQSection() {
  const { theme } = useTheme()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="relative overflow-hidden py-16 md:py-20 px-4">

      {/* Content */}
      <div className="container mx-auto relative z-10 max-w-4xl">
        {/* FAQ Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={`rounded-xl p-6 md:p-8 ${
            theme === 'dark' 
              ? 'bg-[#0f1110]/50 backdrop-blur-sm' 
              : 'bg-white/95 backdrop-blur-sm'
          } shadow-2xl`}
        >
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-2xl" style={{ filter: 'hue-rotate(300deg) saturate(1.5)' }}>📌</span>
            <h2 className={`text-2xl md:text-3xl font-bold ${
              theme === 'dark' ? 'text-gold-400' : 'text-gold-600'
            }`}>
              FAQ – CÂU HỎI THƯỜNG GẶP
            </h2>
          </div>

          {/* Questions List */}
          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className={`rounded-lg overflow-hidden transition-all ${
                  theme === 'dark' 
                    ? 'bg-[#0f1110]/40' 
                    : 'bg-white/50'
                }`}
              >
                {/* Question Button */}
                <button
                  onClick={() => toggleQuestion(index)}
                  className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                    theme === 'dark' 
                      ? 'hover:bg-gray-700/60 text-white' 
                      : 'hover:bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-red-500 text-lg flex-shrink-0">❓</span>
                    <span className={`font-semibold text-sm md:text-base ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {faq.question}
                    </span>
                  </div>
                  <i
                    className={`pi transition-transform flex-shrink-0 ml-3 ${
                      openIndex === index ? 'pi-chevron-up' : 'pi-chevron-down'
                    } ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
                  ></i>
                </button>

                {/* Answer */}
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className={`px-4 pb-4 pt-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      } text-sm md:text-base leading-relaxed`}>
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

